import React from 'react';
import { ICanViewArgs } from '../utils/canView';
import { Button, ButtonLayouts } from '@food/ui';
import { openRenewModal } from '@food/auth/build/dist/utils/renewModal';
import { cns, currySC, registerClass } from '@food/css-manager';
import { useTranslation } from 'react-i18next';
import { Magazine, User, UserRole, VisibilityType } from '../server-types';
import { config } from '../utils/config';
import { DefaultLayout } from '../utils/layout';
import { TypeformModalForm } from './TypeformModalForm';
import { useHistory } from 'react-router';
import { PardotModalForm } from './PardotModalForm';
import gdoIcon from '../static/gdo.svg';

// visibilityArgs.visibility === VisibilityType.User;
interface NotVisibleActionProps {
	visibilityArgs:
	| ({
		visibility: VisibilityType.User;
		user: Pick<User, 'role'> | null;
	} & Pick<ICanViewArgs, 'args' | 'isLogged'>)
	| ({
		visibility: Exclude<VisibilityType, VisibilityType.User>;
	} & Pick<ICanViewArgs, 'args' | 'isLogged'>);
	magazine: Magazine;
	className?: string;
	layout: DefaultLayout | string | undefined;
}

const styleClass = registerClass(
	(t, sc) => `
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin-top: ${t.ratios.xl * t.font.baseSize}px;
	line-height: ${t.ratios.xl};

	.${sc('actionWrapper')} {
		margin-top: ${t.ratios.l}em;
		&:first-child {
			margin-top: 0;
		}
		display: flex;
		flex-direction: column;
		align-items: flex-start;

		> span {
			font-size: 90%;
			display: block;
			
			a {
				text-decoration: underline;
			}
		}

		.${sc('action-label')} {
			font-weight: bold;
			font-size: 0.95em;
		}

		.${sc('button')} {
			margin-top: ${t.ratios.xs * t.font.baseSize * 0.8}px;
		}

		.${sc('gdo-cta-exp')} {
			display: flex;
			align-items: center;
			font-size: 0.9em;
			line-height: 1;

			&:before {
				content: " ";
				display: block;
				width: 1.8em;
				height: 1.8em;
				background-image: url(${gdoIcon});
				background-size: contain;
				background-position: left center;
				background-repeat: no-repeat;
			}

			span {
				display: block;
				margin-left: 8px;
				padding-top: 0.5em;
			}

			u {
				font-weight: bold;
				color: rgb(49,77,103);
			}
		}

		.${sc('kc-cta')} {
			margin-top: 12px;
			font-size: 0.9em;
			line-height: 1;

			u {
				font-weight: bold;
				color: rgb(49,77,103);
			}
		}
	}
	
	.${sc('button')} {
		line-height: 1;
		padding: 0.5em 1.2em;
		border-radius: ${t.radius * t.font.baseSize}px;
		font-weight: ${t.font.weights.bold};
		color: ${t.colors.white};
		display: inline-flex;
		margin-top: ${t.ratios.xs * t.font.baseSize}px;
		
		:first-child {
			margin-top: 0;
		}
	}
	
	.${sc('buttonYellow')} {
		background-color: ${t.colors.alt2.plain};
	}
	
	.${sc('buttonGreen')} {
		background-color: ${t.colors.success.plain};
	}

	.${sc('buttonBlue')} {
		background-color: ${t.colors.alt.dark};
	}

	&.${sc('layout-mobile')}, &.${sc('layout-tablet')} {
		align-items: center;
		.${sc('actionWrapper')} {
			align-items: center;
		}
	}
	
	.${sc('fluent-html')} a {
		text-decoration: underline;
		color: ${t.colors.primary.plain};
	}
`,
);

const sc = currySC(styleClass);

export const NotVisibleAction: React.FC<NotVisibleActionProps> = ({
	visibilityArgs,
	magazine,
	className,
	layout,
}) => {
	const { t } = useTranslation();
	const history = useHistory();

	let actionComponent: any = null;

	/*if (visibility !== 'ANONYMUS' && !isLogged) {
		actionComponent = (
			<Button
				className={cns(sc('button'), sc('buttonBlue'))}
				onClick={() => openRenewModal()}
				label={'login'}
			/>
		);
	}*/

	if (visibilityArgs.visibility === VisibilityType.SfItem) {
		if (visibilityArgs.isLogged) {
			actionComponent = (
				<>
					<div className={sc('actionWrapper')}>
						<span>{t(`magazine_action_subscribe`)}</span>
						<Button
							layout={ButtonLayouts.BUTTON}
							className={cns(sc('button'), sc('buttonBlue'))}
							label={t`subscribe`}
							onClick={() => document.location.href = document.location.origin + config.SUBSCRIBE_PATH}
						/>
					</div>
					<div className={sc('actionWrapper')}>
						<div
							className={sc('fluent-html')}
							dangerouslySetInnerHTML={{
								__html: t(
									`check_email_action_html${config.TARGET_SITE === 'FOODSERVICEWEB'
										? '_FOODSERVICE'
										: ''
									}`,
									{
										supportEmail: config.SUPPORT_EMAIL,
									},
								),
							}}
						/>
					</div>
				</>
			);
		} else {
			actionComponent = (
				<>
					<div className={sc('actionWrapper')}>
						<span className={sc('action-label')}>{t(`magazine_action_login`)}</span>
						<Button
							className={cns(sc('button'), sc('buttonBlue'))}
							onClick={() =>
								openRenewModal({
									redirectOnLogin: '/magazines/' + magazine.id + '/read',
								})
							}
							label={t`login`}
						/>
					</div>
					{config.TARGET_SITE === 'FOODWEB' && (
						<div className={sc('actionWrapper')}>
							<span className={sc('action-label')}>Prima volta qui?</span>
							<div className={sc('gdo-cta-exp')}>
								<a href='https://www.foodweb.it/food-knowledge-center-per-i-professionisti-della-gdo/'>
									<span>Lavori nella GDO? <u>Clicca qui</u> per ottenere l'accesso gratuito </span>
								</a>
							</div>
							<div className={sc('kc-cta')}>
								<a href='https://www.foodweb.it/abbonati-a-food/'>Cosa offre il Knowledge Center? <u>Scoprilo qui</u></a>
							</div>
						</div>
					)}
				</>
			);
		}
	}

	if (visibilityArgs.visibility === VisibilityType.SfCampaign) {
		actionComponent = (
			<div className={sc('actionWrapper')}>
				<span>
					{t('no_campaign_message', {
						supportEmail: config.SUPPORT_EMAIL,
					})}
				</span>
			</div>
		);
	}

	if (
		visibilityArgs.visibility === VisibilityType.Typeform &&
		typeof visibilityArgs.args === 'string'
	) {
		actionComponent = (
			<div className={sc('actionWrapper')}>
				<span>{t(`magazine_action_typeform`)}</span>
				<TypeformModalForm
					achievement={visibilityArgs.args}
					onSubmit={() => history.push((window.location.hash.substr(-1, 1) == '/' ? '' : '/') + 'read')}
				>
					{(setModalOpen) => (
						<Button
							className={cns(sc('button'), sc('buttonBlue'))}
							label={t`compile_form_typescript`}
							onClick={() => setModalOpen(true)}
						/>
					)}
				</TypeformModalForm>
			</div>
		);
	}

	if (visibilityArgs.visibility === VisibilityType.Pardot) {
		actionComponent = (
			<div className={sc('actionWrapper')}>
				<span>{t(`magazine_action_typeform`)}</span>
				<PardotModalForm
					url={config.PARDOT_NEWSLETTER_FORM}
					onSubmit={() => history.push((window.location.hash.substr(-1, 1) == '/' ? '' : '/') + 'read')}
				>
					{(setModalOpen) => (
						<Button
							label={t`compile_form_pardot`}
							onClick={() => setModalOpen(true)}
							className={cns(sc('button'), sc('buttonBlue'))}
						/>
					)}
				</PardotModalForm>
			</div>
		);
	}

	if (visibilityArgs.visibility === VisibilityType.SfCampaignPresente) {
		actionComponent = (
			<div className={sc('actionWrapper')}>
				<span>
					{t('no_campaign_presente_message', {
						supportEmail: config.SUPPORT_EMAIL,
					})}
				</span>
			</div>
		);
	}

	if (visibilityArgs.visibility === VisibilityType.User) {
		if (visibilityArgs.isLogged) {
			if (visibilityArgs.user?.role === UserRole.Consumer) {
				actionComponent = (
					<div className={sc('actionWrapper')}>
						<span>{t(`magazine_action_upgrade_to_${visibilityArgs.args}`)}</span>
						<Button
							className={cns(sc('button'), sc('buttonBlue'))}
							label={t(`upgrade_to_${visibilityArgs.args}`)}
							onClick={() =>
								openRenewModal({
									instantUpgradeRole: visibilityArgs.args as UserRole,
									redirectOnLogin: '/magazine/' + magazine.id + '/read',
								})
							}
						/>
					</div>
				);
			} else {
				return (
					<div className={cns(styleClass, sc('layout-' + layout), className)}>
						<div className={sc('actionWrapper')}>
							<span
								dangerouslySetInnerHTML={{
									__html: t(`not_adequate_role_${visibilityArgs.args}`, {
										supportEmail: config.SUPPORT_EMAIL,
									}),
								}}
							/>
						</div>
					</div>
				);
			}
		} else {
			actionComponent = (
				<>
					<div className={cns(sc('actionWrapper'))}>
						<span>{t(`magazine_action_login`)}</span>
						<Button
							className={cns(sc('button'), sc('buttonBlue'))}
							onClick={() =>
								openRenewModal({
									redirectOnLogin: '/magazine/' + magazine.id + '/read',
								})
							}
							label={t`login`}
						/>
					</div>
					<div className={sc('actionWrapper')}>
						<div
							className={sc('fluent-html')}
							dangerouslySetInnerHTML={{
								__html: t(`check_email_action_html`, {
									supportEmail: config.SUPPORT_EMAIL,
								}),
							}}
						/>
					</div>
				</>
			);
		}
	}

	return (
		<div className={cns(styleClass, sc('layout-' + layout), className)}>
			{magazine.previewUrl && (
				<Button
					linkTo={`/magazine/${magazine.id}/preview`}
					layout={ButtonLayouts.BUTTON}
					className={cns(sc('button'), sc('buttonBlue'))}
					label={t`read_preview`}
				/>
			)}
			{actionComponent}
		</div>
	);
};
